import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Textarea } from '@vaiu/ui/textarea';
import { Badge } from '@vaiu/ui/badge';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Separator } from '@vaiu/ui/separator';
import { 
  Store, 
  MapPin, 
  Search, 
  Plus, 
  Star, 
  TrendingUp, 
  Users, 
  Gift, 
  Target, 
  CheckCircle,
  Clock,
  XCircle,
  ArrowLeft,
  Send,
  Heart,
  Sparkles,
  Trophy,
  Coins,
  Building,
  Coffee,
  ShoppingBag,
  Utensils,
  Wrench,
  Palette,
  Briefcase,
  Zap,
  Share2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@vaiu/ui/select';

interface MerchantRequest {
  id: string;
  businessName: string;
  businessCategory: string;
  location?: string;
  reason: string;
  status: 'pending' | 'contacted' | 'onboarded' | 'declined';
  requestedAt: Date;
  estimatedRevenue?: number;
  priority: number;
}

interface NetworkStats {
  totalRequests: number;
  successfulOnboardings: number;
  rewardsEarned: number;
  advocateLevel: 'bronze' | 'silver' | 'gold' | 'platinum';
  nextLevelRequirement: number;
}

const businessCategories = [
  { value: 'restaurant', label: 'Restaurant & Food', icon: Utensils },
  { value: 'retail', label: 'Retail & Shopping', icon: ShoppingBag },
  { value: 'coffee', label: 'Coffee & Café', icon: Coffee },
  { value: 'services', label: 'Professional Services', icon: Briefcase },
  { value: 'beauty', label: 'Beauty & Wellness', icon: Palette },
  { value: 'automotive', label: 'Automotive', icon: Wrench },
  { value: 'entertainment', label: 'Entertainment', icon: Star },
  { value: 'health', label: 'Healthcare', icon: Heart },
  { value: 'education', label: 'Education', icon: Building },
  { value: 'other', label: 'Other', icon: Store }
];

const MerchantRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState<'request' | 'my-requests' | 'rewards'>('request');
  const [loading, setLoading] = useState(false);
  const [myRequests, setMyRequests] = useState<MerchantRequest[]>([]);
  const [networkStats, setNetworkStats] = useState<NetworkStats | null>(null);
  
  // Request form state
  const [formData, setFormData] = useState({
    businessName: '',
    businessCategory: '',
    location: '',
    reason: '',
    website: '',
    estimatedRevenue: ''
  });

  useEffect(() => {
    loadUserRequests();
    loadNetworkStats();
  }, []);

  const loadUserRequests = async () => {
    try {
      const requests = await platformApi.get('/merchant-requests');
      setMyRequests(requests);
    } catch (error) {
      console.error('Failed to load merchant requests:', error);
    }
  };

  const loadNetworkStats = async () => {
    try {
      const stats = await platformApi.get('/network/stats');
      setNetworkStats(stats);
    } catch (error) {
      console.error('Failed to load network stats:', error);
      // Set default stats if API fails
      setNetworkStats({
        totalRequests: 0,
        successfulOnboardings: 0,
        rewardsEarned: 0,
        advocateLevel: 'bronze',
        nextLevelRequirement: 5
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.businessName || !formData.businessCategory) {
      toast({
        title: "Missing Information",
        description: "Please provide business name and category.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      await platformApi.post('/merchant-requests', {
        businessName: formData.businessName,
        businessCategory: formData.businessCategory,
        location: formData.location,
        reason: formData.reason,
        website: formData.website,
        estimatedRevenue: formData.estimatedRevenue ? parseFloat(formData.estimatedRevenue) : undefined
      });

      toast({
        title: "Request Submitted! 🎉",
        description: `Your request for ${formData.businessName} has been submitted. You'll earn rewards when they join!`,
      });

      // Reset form
      setFormData({
        businessName: '',
        businessCategory: '',
        location: '',
        reason: '',
        website: '',
        estimatedRevenue: ''
      });

      // Reload data
      loadUserRequests();
      loadNetworkStats();

      // Switch to my requests tab
      setActiveTab('my-requests');

    } catch (error: any) {
      toast({
        title: "Submission Failed",
        description: error.message || "Failed to submit merchant request. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'contacted': return <Send className="w-4 h-4 text-blue-500" />;
      case 'onboarded': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'declined': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'onboarded': return 'bg-green-100 text-green-800';
      case 'declined': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'bronze': return <Trophy className="w-4 h-4 text-orange-600" />;
      case 'silver': return <Trophy className="w-4 h-4 text-gray-400" />;
      case 'gold': return <Trophy className="w-4 h-4 text-yellow-500" />;
      case 'platinum': return <Trophy className="w-4 h-4 text-purple-500" />;
      default: return <Trophy className="w-4 h-4 text-gray-400" />;
    }
  };

  const shareRequest = (request: MerchantRequest) => {
    const shareText = `I've requested ${request.businessName} to join! Help me by sharing this request with your network. Together we can help businesses save money and grow! #SupportLocal`;
    
    if (navigator.share) {
      navigator.share({
        title: `Support ${request.businessName}`,
        text: shareText,
        url: window.location.href
      });
    } else {
      // Fallback to clipboard
      navigator.clipboard.writeText(shareText);
      toast({
        title: "Copied to Clipboard",
        description: "Share text has been copied. Paste it on your social media!",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Network Builder</h1>
                <p className="text-gray-600">Request merchants to join Vaiu and earn rewards</p>
              </div>
            </div>
            
            {networkStats && (
              <div className="flex items-center space-x-2">
                {getLevelIcon(networkStats.advocateLevel)}
                <Badge variant="outline" className="capitalize">
                  {networkStats.advocateLevel} Advocate
                </Badge>
              </div>
            )}
          </div>
        </div>

        {/* Network Stats */}
        {networkStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-r from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Total Requests</p>
                    <p className="text-2xl font-bold text-blue-900">{networkStats.totalRequests}</p>
                  </div>
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800">Successful</p>
                    <p className="text-2xl font-bold text-green-900">{networkStats.successfulOnboardings}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-800">Rewards Earned</p>
                    <p className="text-2xl font-bold text-purple-900">{networkStats.rewardsEarned}</p>
                  </div>
                  <Coins className="w-8 h-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-r from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-800">Next Level</p>
                    <p className="text-2xl font-bold text-orange-900">{networkStats.nextLevelRequirement}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex space-x-1 mb-6">
          <Button
            variant={activeTab === 'request' ? 'default' : 'outline'}
            onClick={() => setActiveTab('request')}
            className="flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Request Merchant</span>
          </Button>
          <Button
            variant={activeTab === 'my-requests' ? 'default' : 'outline'}
            onClick={() => setActiveTab('my-requests')}
            className="flex items-center space-x-2"
          >
            <Store className="w-4 h-4" />
            <span>My Requests ({myRequests.length})</span>
          </Button>
          <Button
            variant={activeTab === 'rewards' ? 'default' : 'outline'}
            onClick={() => setActiveTab('rewards')}
            className="flex items-center space-x-2"
          >
            <Gift className="w-4 h-4" />
            <span>Rewards</span>
          </Button>
        </div>

        {/* Request Form */}
        {activeTab === 'request' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Store className="w-5 h-5" />
                <span>Request a Business to Join Vaiu</span>
              </CardTitle>
              <p className="text-sm text-gray-600">
                Help businesses you love save money and grow their customer base. You'll earn rewards when they join!
              </p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name *</Label>
                    <Input
                      id="businessName"
                      value={formData.businessName}
                      onChange={(e) => setFormData(prev => ({ ...prev, businessName: e.target.value }))}
                      placeholder="e.g., Joe's Coffee Shop"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="businessCategory">Category *</Label>
                    <Select
                      value={formData.businessCategory}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, businessCategory: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {businessCategories.map(category => (
                          <SelectItem key={category.value} value={category.value}>
                            <div className="flex items-center space-x-2">
                              <category.icon className="w-4 h-4" />
                              <span>{category.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., Downtown Seattle, WA"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="website">Website (optional)</Label>
                    <Input
                      id="website"
                      value={formData.website}
                      onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="e.g., https://joescoffee.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Why should they join Vaiu? *</Label>
                  <Textarea
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Tell us why this business would benefit from Vaiu. What challenges do they face? How could Vaiu help them grow?"
                    rows={3}
                    required
                  />
                </div>

                <Alert>
                  <Sparkles className="w-4 h-4" />
                  <AlertDescription>
                    <strong>You'll earn rewards when they join!</strong> Successful merchant onboarding earns you points that can be redeemed for cash, credits, or exclusive offers.
                  </AlertDescription>
                </Alert>

                <Button type="submit" disabled={loading} className="w-full">
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Submitting Request...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* My Requests */}
        {activeTab === 'my-requests' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Store className="w-5 h-5" />
                <span>My Merchant Requests</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {myRequests.length === 0 ? (
                <div className="text-center py-8">
                  <Store className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No merchant requests yet.</p>
                  <p className="text-sm text-gray-500 mb-4">Start by requesting your favorite businesses to join Vaiu!</p>
                  <Button onClick={() => setActiveTab('request')}>
                    <Plus className="w-4 h-4 mr-2" />
                    Make Your First Request
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {myRequests.map((request) => (
                    <div key={request.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="font-semibold text-lg">{request.businessName}</h3>
                            <Badge className={`${getStatusColor(request.status)} border-0`}>
                              <div className="flex items-center space-x-1">
                                {getStatusIcon(request.status)}
                                <span className="capitalize">{request.status}</span>
                              </div>
                            </Badge>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                            <div className="flex items-center space-x-1">
                              <Store className="w-4 h-4" />
                              <span className="capitalize">{request.businessCategory}</span>
                            </div>
                            {request.location && (
                              <div className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4" />
                                <span>{request.location}</span>
                              </div>
                            )}
                          </div>
                          
                          <p className="text-sm text-gray-700 mb-2">{request.reason}</p>
                          
                          <p className="text-xs text-gray-500">
                            Requested {new Date(request.requestedAt).toLocaleDateString()}
                          </p>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => shareRequest(request)}
                          >
                            <Share2 className="w-4 h-4 mr-1" />
                            Share
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rewards */}
        {activeTab === 'rewards' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Gift className="w-5 h-5" />
                <span>Reward System</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold mb-4">How You Earn Rewards</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Send className="w-6 h-6 text-blue-600" />
                      </div>
                      <h4 className="font-medium">Request Merchants</h4>
                      <p className="text-sm text-gray-600">10-50 points per request</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <h4 className="font-medium">Successful Onboarding</h4>
                      <p className="text-sm text-gray-600">500-2000 points bonus</p>
                    </div>
                    <div className="text-center">
                      <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                        <Users className="w-6 h-6 text-purple-600" />
                      </div>
                      <h4 className="font-medium">Referral Success</h4>
                      <p className="text-sm text-gray-600">200 points per referral</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-4">Advocate Levels</h3>
                  <div className="space-y-3">
                    {[
                      { level: 'bronze', requirement: '0-4 successful onboardings', benefits: 'Basic rewards, community access' },
                      { level: 'silver', requirement: '5-14 successful onboardings', benefits: 'Bonus multipliers, exclusive offers' },
                      { level: 'gold', requirement: '15-29 successful onboardings', benefits: 'Premium rewards, early access' },
                      { level: 'platinum', requirement: '30+ successful onboardings', benefits: 'Maximum rewards, VIP support' }
                    ].map((tier) => (
                      <div key={tier.level} className="flex items-center space-x-3 p-3 border rounded-lg">
                        {getLevelIcon(tier.level)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium capitalize">{tier.level}</span>
                            <span className="text-sm text-gray-600">({tier.requirement})</span>
                          </div>
                          <p className="text-sm text-gray-600">{tier.benefits}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MerchantRequestsPage; 